import { useMemo } from 'react';
import {
  deriveSeatPriceZonesFromSeats,
  getSeatPriceZonesForPassengersLeg,
} from 'utils/seatPricing';
import { isSeatBasedPricingEnabledForWay } from 'utils/showPricesByGroup';
import {
  getEffectiveSeatPriceZones,
  getPassengersAsList,
  getWaySeatsFallback,
} from 'utils/passengerSeatPricing';

/**
 * Resolve seat pricing zones for departure/return legs with memoized calculations.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state from store.
 * @param {object} params.fields - Redux-form passengers fields.
 * @param {boolean} params.useSeatBasedPricing - Feature flag for seat based pricing.
 * @param {boolean} params.showPricesByGroupGB - GrowthBook value for grouped prices.
 * @param {string|boolean|object} params.showPricesByGroupConfig - Whitelabel config value.
 * @param {object} params.seatsBus - Seats bus state map.
 * @returns {object} Seat pricing flags and effective zones by trip leg.
 */
const usePassengerSeatPriceZones = ({
  purchase = {},
  fields,
  useSeatBasedPricing = false,
  showPricesByGroupGB,
  showPricesByGroupConfig,
  seatsBus = {},
}) => {
  return useMemo(() => {
    const legSeatPricingFlags = {
      useSeatBasedPricing: Boolean(useSeatBasedPricing),
      showPricesByGroupGB: Boolean(showPricesByGroupGB),
      showPricesByGroupConfig,
    };

    const seatBasedPricingDeparture = isSeatBasedPricingEnabledForWay(
      purchase.departs,
      legSeatPricingFlags,
    );
    const seatBasedPricingReturn = isSeatBasedPricingEnabledForWay(
      purchase.returns,
      legSeatPricingFlags,
    );

    const seatPriceZonesDeparture = seatBasedPricingDeparture
      ? getSeatPriceZonesForPassengersLeg(purchase.departs || {}, seatsBus)
      : [];
    const seatPriceZonesReturn = seatBasedPricingReturn
      ? getSeatPriceZonesForPassengersLeg(purchase.returns || {}, seatsBus)
      : [];

    const passengersAsList = getPassengersAsList(fields);
    const departureSeatsFallback = getWaySeatsFallback(passengersAsList, 'departure');
    const returnSeatsFallback = getWaySeatsFallback(passengersAsList, 'return');

    const effectiveSeatPriceZonesDeparture = getEffectiveSeatPriceZones({
      enabled: seatBasedPricingDeparture,
      seatPriceZones: seatPriceZonesDeparture,
      deriveFromSeats: deriveSeatPriceZonesFromSeats,
      fallbackSeats: departureSeatsFallback,
    });
    const effectiveSeatPriceZonesReturn = getEffectiveSeatPriceZones({
      enabled: seatBasedPricingReturn,
      seatPriceZones: seatPriceZonesReturn,
      deriveFromSeats: deriveSeatPriceZonesFromSeats,
      fallbackSeats: returnSeatsFallback,
    });

    return {
      seatBasedPricingDeparture,
      seatBasedPricingReturn,
      effectiveSeatPriceZonesDeparture,
      effectiveSeatPriceZonesReturn,
    };
  }, [
    fields,
    purchase,
    seatsBus,
    showPricesByGroupConfig,
    showPricesByGroupGB,
    useSeatBasedPricing,
  ]);
};

export default usePassengerSeatPriceZones;
